import { useCallback, useMemo, useState } from 'react';
import { playSound, Sound } from '~/assets/sounds';
import { CountInput } from '~/components/ui/CountInput';
import { Divider } from '~/components/ui/Divider';
import { InputRow } from '~/components/ui/InputRow';
import { MoneyInput } from '~/components/ui/MoneyInput';
import { SliderLarge } from '~/components/ui/SliderLarge';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import { rage, useInputAction } from '~/rage';
import {
	Currency,
	IModalReplyWithValue,
	IModalStateBusinessOrder,
	InputAction,
} from '~/shared';
import { CurrencyIcon } from '../../CurrencyIcon';
import { ModalButton } from '../ModalButton';
import {
	StyledButtonsContainer,
	StyledDescription,
	StyledInputs,
	StyledModalBody,
	StyledSliderRow,
} from './ModalBusinessOrder.styled';

const ModalBusinessOrder: React.FC<{
	state: IModalStateBusinessOrder;
}> = ({ state }) => {
	const currencyIcon = useWalletIcon(Currency.Ruble, true);

	const stock = state?.stock ?? 0;
	const maxCount = state?.maxCount ?? 0;
	const minCount = state?.minCount ?? 1;
	const pricePerItem = state?.pricePerItem ?? 0;
	const unit = state?.unit;

	const [count, setCount] = useState<number>(0);

	const totalPrice = useMemo(() => count * pricePerItem, [count, pricePerItem]);

	const isValid = useMemo(
		() => count >= minCount && count <= maxCount,
		[count, minCount, maxCount],
	);

	const reply = useCallback(
		(isAccepted: boolean) => {
			rage.modal.replyToCurrentModal({
				isAccepted,
				value: isAccepted ? count : 0,
			} as IModalReplyWithValue<number>);
		},
		[count],
	);

	useInputAction(InputAction.CommonMenuAccept, () => {
		if (isValid) {
			reply(true);
		}
	});

	useInputAction(InputAction.CommonMenuClose, () => {
		reply(false);
	});

	const renderCurrencyIcon = (size: 'sm' | 'md' | 'lg' = 'sm') => (
		<CurrencyIcon icon={currencyIcon} size={size} style={{ opacity: 0.5 }} />
	);

	return (
		<StyledModalBody>
			{state?.text && <StyledDescription>{state.text}</StyledDescription>}

			<StyledSliderRow>
				<SliderLarge
					min={0}
					max={maxCount}
					value={count}
					step={1}
					height={50}
					onChange={setCount}
					onMax={() => setCount(maxCount)}
				/>
				<CountInput
					value={count}
					onChange={setCount}
					min={0}
					max={maxCount}
					unit={unit}
					height={50}
					width={150}
					fontSize={16}
				/>
			</StyledSliderRow>

			<StyledInputs>
				<MoneyInput
					label={state?.stockLabel}
					value={stock}
					onChange={() => undefined}
					unit={unit}
					disabled
					height={50}
					fontSize={16}
					padding="0 20px"
				/>
			</StyledInputs>

			<Divider $direction={'H'} style={{ opacity: 0.1 }} />
			<InputRow
				label={'Макс. кол-во товара'}
				icon={
					unit ? (
						<Text style={{ opacity: 0.5, color: '#ffffff' }}>{unit}</Text>
					) : undefined
				}
			>
				<Text>{maxCount.toLocaleString('ru-RU')}</Text>
			</InputRow>
			<Divider $direction={'H'} style={{ opacity: 0.1 }} />
			<InputRow label={'Стоимость товара'} icon={renderCurrencyIcon('md')}>
				<Text>{totalPrice.toLocaleString('ru-RU')}</Text>
			</InputRow>

			<StyledButtonsContainer>
				<ModalButton
					$type={state?.cancelButton.color}
					onClick={() => {
						reply(false);
						playSound(Sound.ClickButton);
					}}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
				>
					{state?.cancelButton.text}
				</ModalButton>
				<ModalButton
					$type={state?.acceptButton.color}
					disabled={!isValid}
					onClick={() => {
						if (!isValid) {
							return;
						}
						reply(true);
						playSound(Sound.ClickButton);
					}}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
				>
					{state?.acceptButton.text}
				</ModalButton>
			</StyledButtonsContainer>
		</StyledModalBody>
	);
};

export { ModalBusinessOrder };
